import React, {
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import LinkButton from 'components/LinkButton';
import PagePagination from 'components/PagePagination';
import LoadMask from 'components/LoadMask';
import UpgradeRecruiterAccount from 'scenes/Dialog/components/UpgradeRecruiterAccount';
import FiltersContentLoader from 'components/FiltersContentLoader';

import { LinkBuilder } from 'helpers/link-builder';
import { useGrid } from 'hooks/useGrid';
import { ENDPOINT } from 'constants/endpoints';
import { ROUTE } from 'constants/route';

import './styles.css';

const InBoundsHistory = forwardRef((props, ref) => {
  const { isCandidate = false, isRecruiter = false } = props;

  const endpoint =
    isCandidate && !isRecruiter
      ? ENDPOINT.COLLECTION.INBOUNDS_HISTORY_CANDIDATE
      : ENDPOINT.COLLECTION.INBOUNDS_HISTORY_RECRUITER;

  const {
    data: {
      isFetching,
      page,
      pagesCount,
      perPage,
      rows,
      rowsCount,
      unRepliedCount,
    },
    actions: { readPage, fetch },
  } = useGrid(endpoint, null, false);

  useImperativeHandle(ref, () => ({
    refresh: () => fetch(),
  }));

  useEffect(() => {
    fetch();
  }, []);

  const inbounds = useMemo(
    () =>
      rows.map(inbound => {
        let link = null;
        if (isRecruiter) {
          link =
            inbound.AllowView === 'Yes' ? (
              <LinkButton
                text={inbound.FullName}
                to={LinkBuilder.getProfileLink(inbound.CandidateID)}
                btnFontWeight="600"
                btnFontSize="0.8571rem"
                underline={false}
                btnFontFamily="Noto Sans"
              />
            ) : (
              <LinkButton
                text={inbound.FullName}
                controlFunc={() => UpgradeRecruiterAccount()}
                btnFontWeight="600"
                btnFontSize="0.8571rem"
                underline={false}
                btnFontFamily="Noto Sans"
              />
            );
        } else {
          link =
            inbound.CompanyActive === 'Yes' ? (
              <LinkButton
                text={inbound.CompanyName}
                to={LinkBuilder.getFirmLink(inbound.CompanyUrlAlias)}
                btnFontWeight="600"
                btnFontSize="0.8571rem"
                underline={false}
                btnFontFamily="Noto Sans"
              />
            ) : (
              <strong>{inbound.CompanyName}</strong>
            );
        }

        const status =
          inbound.MessageID && inbound.MessageFolder && isCandidate ? (
            <LinkButton
              text={inbound.Status}
              btnFontWeight="400"
              btnFontSize="0.8571rem"
              to={{
                pathname: ROUTE.INBOX,
                state: {
                  initialParams: {
                    message: inbound.MessageID,
                    folder: inbound.MessageFolder,
                  },
                },
              }}
            />
          ) : (
            inbound.Status
          );

        return (
          <div
            className="sidebar__table-row"
            key={inbound.ApplicationInboundID}
          >
            <div className="sidebar__table-column">{link}</div>
            <div className="sidebar__table-column">
              <div className="sidebar__text">{status}</div>
            </div>
            <div className="sidebar__table-column">
              <div className="sidebar__text">{inbound.SentDate}</div>
            </div>
          </div>
        );
      }),
    [rows]
  );

  return !isFetching ? (
    !!Number(rowsCount) && (
      <div className="sidebar__widget">
        {isFetching && <LoadMask relative />}
        <div className="sidebar__block">
          <h4 className="sidebar__title">InBounds History</h4>
        </div>

        {isRecruiter && !!Number(unRepliedCount) && (
          <div className="sidebar__block">
            <p className="sidebar__subtitle">
              <LinkButton text={unRepliedCount} to={ROUTE.SEARCH_INBOUNDS} />
              {` `}
              InBounds have not been replied to.
            </p>
          </div>
        )}

        <div className="sidebar__block">
          <div className="sidebar__table">
            <div className="sidebar__table-row sidebar__table-row--head">
              <div className="sidebar__table-column sidebar__table-column--head">
                {isCandidate ? 'Company' : 'Candidate'}
              </div>
              <div className="sidebar__table-column sidebar__table-column--head">
                Status
              </div>
              <div className="sidebar__table-column sidebar__table-column--head">
                Date
              </div>
            </div>
            {inbounds}
            {pagesCount > 1 && (
              <PagePagination
                page={page}
                perPage={perPage}
                count={rowsCount}
                pageRangeDisplayed={3}
                controlFunc={readPage}
                hideFirstLastPages={false}
                hideNavigation={true}
                minimalStyle={true}
                scrollToTop={false}
              />
            )}
          </div>
        </div>
      </div>
    )
  ) : (
    <FiltersContentLoader />
  );
});

InBoundsHistory.propTypes = {
  isCandidate: props =>
    !props.isCandidate && !props.isRecruiter
      ? new Error(
          `One of props 'isCandidate' or 'isRecruiter' was not specified in 'InBoundsHistory'.`
        )
      : null,
  isRecruiter: props =>
    !props.isCandidate && !props.isRecruiter
      ? new Error(
          `One of props 'isCandidate' or 'isRecruiter' was not specified in 'InBoundsHistory'.`
        )
      : null,
};

export default InBoundsHistory;
